import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { NgSelectComponent } from "@ng-select/ng-select";
import {
  LoadingController,
  NavController,
  NavParams,
  ToastController,
  ViewController,
} from "ionic-angular";
import { ScriptSureAllergy } from "models/scriptSure/scriptSureAllergyResponse";
import * as moment from "moment-timezone";
import { MedicationDataService } from "providers/order-data-service/medications-data-service";
import {
  AllergyTypes,
  PatientAllergiesService,
} from "providers/patient-charts/patient-allergies-service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { Observable, Subject } from "rxjs";
import { of } from "rxjs/observable/of";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
} from "rxjs/operators";
import { Subscription } from "rxjs/Subscription";
import { Patient } from "../../../../models/patient-chart/patient";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import {
  AllergyLookup,
  PatientAllergy,
  PatientAllergyDelete,
  PatientAllergyEdit,
} from "../patient-allergies";

interface DropdownItem {
  label: string;
  value: string;
  scriptSureAllergy?: ScriptSureAllergy;
}

@Component({
  selector: "allergy-edit",
  templateUrl: "allergy-edit.html",
})
export default class AllergyEditPage implements OnInit, OnDestroy {
  @ViewChild("fieldAllergyNameId") allergyNameInput: NgSelectComponent;
  @ViewChild("fieldTypeId") typeInput: NgSelectComponent;
  @ViewChild("hiddenInputAllergyName") hiddenInputAllergyName: {
    setFocus: () => void;
  };
  @ViewChild("hiddenInputType") hiddenInputType: { setFocus: () => void };
  @ViewChild("reactionInputId") reactionInput: NgSelectComponent;
  @ViewChild("severityInputId") severityInput: NgSelectComponent;

  allergies: AllergyLookup[] = [];
  allergiesLoading = false;
  allergiesSavedList: PatientAllergy[] = [];
  allergiesTypeList: string[] = [];
  allergiesTypes: AllergyTypes;
  allergy: PatientAllergy;
  allergyCreate: boolean = true;
  allergyDropdownItems$: Observable<DropdownItem[]> = of([]);
  allergyForm: FormGroup;
  allergyQuery$ = new Subject<string>();
  datePlacementPreference: PlacementArray = ["bottom-left"];
  editAllegery: PatientAllergy;
  existingAllergy: PatientAllergy;
  headerLabel: string = "Add Allergy";
  isDelete = false;
  isDuplicate = false;
  isEdit = false;
  orderDetails: FormGroup;
  patientChart: Patient;
  patientChartId: number;
  reactionIsOther = false;
  reactionList: DropdownItem[] = [
    { label: "Unknown", value: "0" },
    { label: "Skin Rash", value: "1" },
    { label: "Hives", value: "2" },
    { label: "Respiratory Distress", value: "3" },
    { label: "Laryngeal Edema", value: "4" },
    { label: "Syncope", value: "6" },
    { label: "Anaphylaxis", value: "7" },
    { label: "Asthmatic Attack", value: "5" },
    { label: "Other", value: "8" },
    { label: "Diarrhea", value: "9" },
    { label: "Dizziness", value: "10" },
    { label: "Wheezing", value: "11" },
  ];
  reactionSelected: DropdownItem;
  saveLabel: string = "Save";
  selectedAllergy: ScriptSureAllergy;
  severityList: DropdownItem[] = [
    { label: "Mild", value: "6" },
    { label: "Moderate", value: "4" },
    { label: "Severe", value: "2" },
  ];
  severitySelected: DropdownItem;
  spinner: any = null;
  spinnerCounter: number = 0;
  subscriptions: Subscription[] = [];
  tenantId: number;

  public constructor(
    private allergiesService: PatientAllergiesService,
    private fb: FormBuilder,
    private loadingCtrl: LoadingController,
    private medicationDataService: MedicationDataService,
    private navParams: NavParams,
    private toastCtrl: ToastController,
    public navCtrl: NavController,
    public referralsService: ReferralsServiceProvider,
    public stateData: StateManagementService,
    public viewCtrl: ViewController
  ) {
    this.patientChartId = parseInt(this.navParams.data.patientChartId);
    this.isDelete = this.navParams.data.isDelete || false;
    this.isEdit = this.navParams.data.isEdit || false;
    this.allergiesSavedList = this.navParams.data.allergiesList || [];
    if (this.isDelete || this.isEdit) {
      this.existingAllergy = this.navParams.data.allegeryItem;
    }
    this.loadAllergiesTypeList();
    this.allergyForm = new FormGroup({
      allergyName: this.fb.control(null, Validators.required),
      allergyType: this.fb.control(null),
      reaction: this.fb.control(null),
      reactionOther: this.fb.control("", (control) =>
        this.reactionIsOther ? Validators.required(control) : null
      ),
      severity: this.fb.control(null),
      createdTimezone: this.fb.control(moment.tz.guess()),
    });
    if (this.isEdit) {
      this.headerLabel = "Edit Allergy";
      this.allergyForm.addControl(
        "reasonForChange",
        new FormControl("", [Validators.required])
      );
    } else if (this.isDelete) {
      this.saveLabel = "Confirm";
      this.headerLabel = "Remove Allergy";
      this.allergyForm.addControl(
        "reasonForChange",
        new FormControl("", [Validators.required])
      );
      delete this.allergyForm.controls["allergyType"];
      delete this.allergyForm.controls["allergyName"];
      delete this.allergyForm.controls["reaction"];
      delete this.allergyForm.controls["reactionOther"];
      delete this.allergyForm.controls["severity"];
    } else {
      this.headerLabel = "Add Allergy";
    }
    this.isDuplicate = false;
  }

  ngOnInit(): void {
    this.initAllergySearch();
    if (this.isEdit) {
      this.setEditValues();
    }
  }

  setReactionDropdownItemOnEditAllergyFormLoad(reactionId: number) {
    const reactionDropdownItem = this.reactionList.find(
      (reaction) => reaction.value === `${reactionId}`
    );
    if (reactionDropdownItem) {
      this.onReactionChange(reactionDropdownItem);
      this.allergyForm.controls.reaction.setValue(`${reactionId}`);
    } else {
      this.allergyForm.controls.reaction.setValue(null);
    }
  }

  setSeverityDropdownItemOnEditAllergyFormLoad(severityId: number) {
    const severityDropdownItem = this.severityList.find(
      (severity) => severity.value === `${severityId}`
    );
    if (severityDropdownItem) {
      this.onSeverityChange(severityDropdownItem);
      this.allergyForm.controls.severity.setValue(`${severityId}`);
    } else {
      this.allergyForm.controls.severity.setValue(null);
    }
  }

  setEditValues() {
    // Set ScriptSureData values on edit form
    if (
      this.existingAllergy.scriptSureData &&
      this.existingAllergy.scriptSureData.allergyId &&
      this.existingAllergy.scriptSureData.allergyType
    ) {
      const { reactionId, severityId, allergyId, allergyType } =
        this.existingAllergy.scriptSureData;
      this.setReactionDropdownItemOnEditAllergyFormLoad(reactionId);
      this.setSeverityDropdownItemOnEditAllergyFormLoad(severityId);

      this.allergyDropdownItems$.pipe(take(1)).subscribe((list) => {
        this.onAllergySelected(list[0]);
        this.allergyForm.controls.allergyName.setValue(
          `${allergyType}:${allergyId}`
        );
      });
    } else {
      // Allergy without ScriptSureData -- try to find a matching allergy and reaction
      // Starts the search for the existing allergy, but it's up to the user to select it.
      this.allergyForm.controls.allergyName.setValue(null);
      this.allergyQuery$.next(this.existingAllergy.allergyName);
      // Tries to match old reaction. Falls back to empty.
      if (this.existingAllergy.reaction) {
        const reactionItem = this.reactionList.find((reaction) =>
          this.existingAllergy.reaction
            .toLocaleLowerCase()
            .includes(reaction.label.toLocaleLowerCase())
        );
        if (reactionItem) {
          this.onReactionChange(reactionItem);
          this.allergyForm.controls.reaction.setValue(reactionItem.value);
        }
      }
    }
    // Other values on edit form
    this.allergyForm.controls.allergyType.setValue(
      this.existingAllergy.allergyType
    );
    this.allergyForm.controls.reactionOther.setValue(
      this.existingAllergy.reactionOther
    );
  }

  initAllergySearch() {
    let existingDropdownItem: DropdownItem;
    // Legacy (pre-ScriptSure / isLegacyPatient) allergies have no scriptSureData.
    // setEditValues() already handles that case; guard here too so opening the
    // Edit/Remove modal for such an allergy does not NPE and leave a blank modal.
    if (this.existingAllergy && this.existingAllergy.scriptSureData) {
      existingDropdownItem = {
        label: this.existingAllergy.allergyName,
        value: `${this.existingAllergy.scriptSureData.allergyType}:${this.existingAllergy.scriptSureData.allergyId}`,
        scriptSureAllergy: {
          allergyType: this.existingAllergy.scriptSureData.allergyType,
          allergyId: this.existingAllergy.scriptSureData.allergyId,
          ROUTED_MED_ID: this.existingAllergy.scriptSureData.routedMedId,
          Descr: this.existingAllergy.allergyName,
        },
      };
    }

    this.allergyDropdownItems$ = this.allergyQuery$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      tap(() => (this.allergiesLoading = true)),
      switchMap((query: string | null) => {
        // null -> clear, '' -> field cleared by backspace
        if (!query || query.length === 0) {
          return of([]);
        }
        return this.medicationDataService
          .searchAllergies({
            query: {
              name: query ? query : undefined,
            },
          })
          .pipe(
            mergeMap((allergyList) =>
              of(
                allergyList.map((allergy) => {
                  return {
                    label: allergy.Descr,
                    value: `${allergy.allergyType}:${allergy.allergyId}`,
                    scriptSureAllergy: allergy,
                  };
                })
              )
            ),
            catchError(() => of([])), // empty list on error
            tap(() => (this.allergiesLoading = false))
          );
      }),
      startWith(existingDropdownItem ? [existingDropdownItem] : [])
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnClickType() {
    this.hiddenInputType.setFocus();
    this.typeInput.filterInput.nativeElement.focus();
  }

  ngOnClickReaction() {
    this.hiddenInputType.setFocus();
    this.reactionInput.filterInput.nativeElement.focus();
  }

  ngOnClickSeverity() {
    this.hiddenInputType.setFocus();
    this.severityInput.filterInput.nativeElement.focus();
  }

  ngOnClickNameAllergy() {
    this.hiddenInputType.setFocus();
    this.allergyNameInput.filterInput.nativeElement.focus();
  }

  save(): void {
    const dateTimeZone = moment.tz.guess();

    if (this.reactionSelected === null || this.reactionSelected === undefined) {
      this.reactionSelected = null;
    }

    if (this.severitySelected === null || this.severitySelected === undefined) {
      this.severitySelected = null;
    }

    if (this.isEdit) {
      this.increaseSpinnerCount("Updating ...");
      const allergyEdit: PatientAllergyEdit = {
        allergyType: this.allergyForm.controls.allergyType.value,
        allergyId: this.existingAllergy.allergyId,
        allergyName: this.selectedAllergy.Descr,
        reaction: this.reactionSelected
          ? this.reactionSelected.label
          : undefined,
        reactionOther: this.allergyForm.controls.reactionOther.value,
        severity: this.severitySelected
          ? this.severitySelected.label
          : undefined,
        scriptSureData: {
          allergyType: this.selectedAllergy.allergyType,
          allergyId: this.selectedAllergy.allergyId,
          routedMedId: this.selectedAllergy.ROUTED_MED_ID,
          reactionId: this.reactionSelected
            ? parseInt(this.reactionSelected.value)
            : undefined,
          severityId: this.severitySelected
            ? parseInt(this.severitySelected.value)
            : undefined,
        },
        modifiedTimezone: dateTimeZone,
        reasonForChange: this.allergyForm.controls.reasonForChange.value,
      };
      this.allergiesService
        .updateAllergies(this.patientChartId, allergyEdit)
        .pipe(take(1))
        .subscribe(
          (x) => this.handleObservableSuccess(x),
          (err) => this.handleObservableError("updating", err)
        );
    } else if (this.isDelete) {
      this.increaseSpinnerCount("Deleting ...");
      const allergyDelete: PatientAllergyDelete = {
        allergyType: this.existingAllergy.allergyType,
        allergyName: this.existingAllergy.allergyName,
        allergyId: this.existingAllergy.allergyId,
        reasonForDelete: this.allergyForm.controls.reasonForChange.value,
        modifiedTimezone: dateTimeZone,
      };
      this.allergiesService
        .deleteAllergies(this.referralsService.PATIENT_ID, allergyDelete)
        .pipe(take(1))
        .subscribe(
          (x) => this.handleObservableSuccess(x),
          (err) => this.handleObservableError("deleting", err)
        );
    } else {
      this.increaseSpinnerCount("Saving ...");

      const patientAllergy: PatientAllergy = {
        ...this.allergyForm.value,
        allergyName: this.selectedAllergy.Descr,
        reaction: this.reactionSelected
          ? this.reactionSelected.label
          : undefined,
        reactionOther: this.allergyForm.controls.reactionOther.value,
        severity: this.severitySelected
          ? this.severitySelected.label
          : undefined,
        scriptSureData: {
          allergyType: this.selectedAllergy.allergyType,
          allergyId: this.selectedAllergy.allergyId,
          routedMedId: this.selectedAllergy.ROUTED_MED_ID,
          reactionId: this.reactionSelected
            ? parseInt(this.reactionSelected.value)
            : undefined,
          severityId: this.severitySelected
            ? parseInt(this.severitySelected.value)
            : undefined,
        },
      };
      this.allergiesService
        .createAllergies(this.patientChartId, patientAllergy)
        .pipe(take(1))
        .subscribe(
          (x) => this.handleObservableSuccess(x),
          (err) => this.handleObservableError("saving", err)
        );
    }
  }

  handleObservableSuccess(x: any): void {
    this.decreaseSpinnerCount();
    this.navCtrl.pop();
    [];
  }

  handleObservableError(
    action: "deleting" | "saving" | "updating",
    err: any
  ): void {
    this.decreaseSpinnerCount();
    if (err.error === "Allergy duplicated for Patient Allergy") {
      this.isDuplicate = true;
    } else {
      this.presentToast(`Error ${action} allergy`);
    }
  }

  cancel(): void {
    this.viewCtrl.dismiss({ cancel: true });
  }

  presentToast(message) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "error",
    });

    toast.onDidDismiss(() => {});

    toast.present();
  }

  increaseSpinnerCount(message) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  loadAllergiesTypeList() {
    this.allergiesTypes = this.allergiesService.getAllergyTypeList();
    Object.keys(this.allergiesTypes).forEach((element) => {
      const elementCap = element.charAt(0).toUpperCase() + element.slice(1);
      this.allergiesTypeList.push(elementCap.toString());
    });
  }

  onReactionClear(event) {
    this.reactionSelected = undefined;
  }

  onSeverityClear(event) {
    this.severitySelected = undefined;
  }

  onAllergySelected(event?: DropdownItem) {
    if (event) {
      this.selectedAllergy = event.scriptSureAllergy;
      this.checkDuplicate();
    }
  }

  onAllergyTypeChange(event) {}

  onReactionChange(event?: DropdownItem) {
    if (event) {
      this.reactionIsOther = event.value === "8";
      this.allergyForm.controls.reactionOther.setValue(null);
      this.reactionSelected = event;
    }
  }

  onSeverityChange(event?: DropdownItem) {
    if (event) {
      this.severitySelected = event;
    }
  }

  checkDuplicate() {
    this.isDuplicate =
      !this.isEdit &&
      this.allergiesSavedList.some(
        (allergy) =>
          !allergy.isDeleted &&
          allergy.allergyName.toLowerCase() ===
            this.selectedAllergy.Descr.toLowerCase()
      );
  }

  get notFoundText(): string {
    return this.allergiesLoading
      ? "Searching allergies..."
      : "No allergies found.";
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/allergies/allergy-edit/allergy-edit.ts